// How long and when, said ONE way across the Workflows area (runs, the run
// view, jobs), pure so status.selftest.ts runs it on its own.
//
//   durations    words ("42 s", "18 min", "1 h 50 min"), the house
//                formatDuration; the clock form ("4:12", "1:02:15") only
//                for a counter that ticks while you watch.
//   spans        from a start to its end, or to now while it goes: the run
//                view's cards and header and the job page's minutes all
//                measure a thing that way.
//   times of day in the operator's own zone; "19:03 UTC" written by the
//                Lead or the engine into a text is turned into it too.

import { formatDuration, formatTime } from "@/lib/format";

function ms(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : null;
}

/** "42 s", "4:12", "1:02:15": a span in clock form (tabular), for a counter that ticks. "" for nothing. */
export function clockLabel(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds) || seconds < 0) return "";
  const total = Math.floor(seconds);
  if (total < 60) return `${total} s`;
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = h ? String(m).padStart(2, "0") : String(m);
  return `${h ? `${h}:` : ""}${mm}:${String(s).padStart(2, "0")}`;
}

/** A duration in words ("18 min", "1 h 50 min"), or in clock form while it `ticking`. "" for nothing. */
export function durationLabel(seconds: number | null | undefined, { ticking = false }: { ticking?: boolean } = {}): string {
  return ticking ? clockLabel(seconds) : formatDuration(seconds);
}

/** Seconds from `startedAt` to `endedAt` (or to `now` while it has not ended); null before a start. */
export function elapsedSeconds(startedAt: string | null | undefined, endedAt: string | null | undefined, now: Date): number | null {
  const start = ms(startedAt);
  if (start == null) return null;
  const end = ms(endedAt) ?? now.getTime();
  return Math.max(0, (end - start) / 1000);
}

/**
 * How long a thing took ("18 min"), or has been going (ticking, "4:12")
 * while it has no end yet; "" before it started.
 */
export function elapsedLabel(startedAt: string | null | undefined, endedAt: string | null | undefined, now: Date): string {
  return durationLabel(elapsedSeconds(startedAt, endedAt, now), { ticking: ms(endedAt) == null });
}

/** Minutes in words, whole ("12 min", "1 h 5 min"); "under 1 min" below one; "" for nothing. */
export function minutesWords(minutes: number | null | undefined): string {
  if (minutes == null || !Number.isFinite(minutes) || minutes < 0) return "";
  if (minutes > 0 && minutes < 0.5) return "under 1 min";
  return formatDuration(Math.round(minutes) * 60);
}

const UTC_TIME = /\b([01]?\d|2[0-3]):([0-5]\d)\s?UTC\b/g;

/**
 * A text with every "HH:MM UTC" in it turned into the operator's own time
 * of day ("ends by 19:03 UTC" → "ends by 20:03" in London in summer). The
 * Lead and the engine write UTC so agents agree on it; the operator reads
 * their zone. `on` picks the day the times fall on (daylight saving): the
 * text's own date when known, else today.
 */
export function localTimes(text: string, on: Date = new Date()): string {
  if (!text || !text.includes("UTC")) return text;
  return text.replace(UTC_TIME, (_m, h: string, m: string) => {
    const d = new Date(Date.UTC(on.getUTCFullYear(), on.getUTCMonth(), on.getUTCDate(), Number(h), Number(m)));
    return formatTime(d);
  });
}
